import { TableScroll } from '@web/components/table-scroll.tsx';
import type { ReactNode } from 'react';
import { formatCents } from '@shared/calculations/pricing.ts';
import { classes } from '../../components/ui.tsx';

/**
 * The formatting and small presentational pieces the operator's affiliate
 * screens share. A sibling of pages/partner/common.tsx rather than an import
 * of it: the two portals are separate security realms with separate shells,
 * and the partner one is localized while this console is deliberately not —
 * but money and periods must still read identically on both sides of a payout,
 * so these mirror those helpers' behavior exactly.
 */

export function money(cents: number, currency = 'USD'): string {
  return formatCents(cents, currency);
}

export function shortDate(value: string | null): string {
  if (!value) return '—';
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toLocaleDateString() : '—';
}

/** "2026-08" as a person reads it, with the raw value as the fallback. */
export function periodLabel(period: string): string {
  const parsed = Date.parse(`${period}-01T00:00:00Z`);
  if (!Number.isFinite(parsed)) return period;
  return new Date(parsed).toLocaleDateString(undefined, { year: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** The last `count` commission periods, most recent first. */
export function recentPeriods(count: number): string[] {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const month = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - index, 1));
    return month.toISOString().slice(0, 7);
  });
}

export type PillTone = 'neutral' | 'good' | 'warn' | 'bad';

/** Color reinforces the word, never replaces it — the label reads on its own. */
export function Pill({ value, tone = 'neutral' }: { value: string; tone?: PillTone }) {
  const tones = {
    neutral: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
    good: 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
    warn: 'border-[color:var(--color-warning)] text-[color:var(--color-warning)]',
    bad: 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]',
  } as const;
  return <span className={classes('badge', tones[tone])}>{value.replace(/_/g, ' ')}</span>;
}

export function partnerStatusTone(status: string): PillTone {
  switch (status) {
    case 'active':
      return 'good';
    case 'application_pending':
    case 'payout_hold':
    case 'compliance_review':
      return 'warn';
    case 'suspended':
    case 'rejected':
    case 'terminated':
      return 'bad';
    default:
      return 'neutral';
  }
}

export function riskTone(riskState: string): PillTone {
  return riskState === 'normal' ? 'neutral' : riskState === 'review' ? 'warn' : 'bad';
}

export function payoutStatusTone(status: string): PillTone {
  switch (status) {
    case 'paid':
      return 'good';
    case 'open':
    case 'calculated':
    case 'under_review':
    case 'approved':
    case 'processing':
      return 'neutral';
    case 'partially_failed':
    case 'held':
    case 'unclaimed':
    case 'sent':
    case 'pending':
    case 'carried_forward':
      return 'warn';
    default:
      // failed, returned, canceled
      return 'bad';
  }
}

export function TableShell({ label, head, children }: { label: string; head: ReactNode; children: ReactNode }) {
  return (
    <TableScroll aria-label={label} className="card overflow-x-auto p-0">
      {/* `w-full` alone pins the table to the wrapper, so the wrapper never
          has anything to scroll: on a phone the columns squeeze until cells
          break mid-word. `min-w-max` is a no-op while the content fits and
          hands the wrapper a scroll when it does not. */}
      <table className="data-table w-full min-w-max text-left text-sm">
        <thead className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">{head}</thead>
        <tbody>{children}</tbody>
      </table>
    </TableScroll>
  );
}

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th scope="col" className={classes('px-4 py-3 font-medium text-[color:var(--color-muted)]', className)}>
      {children}
    </th>
  );
}

export function Td({ children, className, title }: { children?: ReactNode; className?: string; title?: string }) {
  return (
    <td className={classes('px-4 py-3', className)} title={title}>
      {children}
    </td>
  );
}
